import React, {useCallback, useRef} from "react";
import {FormFieldProps} from "../Form/Form";
import "./file-field.scss";
import {FieldWithLabel} from "../../../hoc/withClass";
import {DropEvent, FileRejection, useDropzone} from "react-dropzone";
import {FileIcon} from "../../icons/icons/Fileicon";
import {useTranslation} from "react-i18next";
import {bytesToMegabytes} from "../../../utils/utils";
import {UploadIcon} from "../../icons/icons/UploadIcon";

interface TextFieldProps extends FormFieldProps {
    onErrorMessage?: string;
    name: string;
}

//TODO move to props
const ACCEPTED_FILES = {
    "application/xml": [".xml", ".osm"],
    "application/gzip": [".gz", ".zip"]
};

const FileField = ({name, required, onChange, ...props}: TextFieldProps) => {
    const hiddenInputRef = useRef<any>(null);
    const {t} = useTranslation();

    const dropHandler = useCallback((acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
        if (hiddenInputRef.current) {
            // Note the specific way we need to munge the file into the hidden input
            // https://stackoverflow.com/a/68182158/1068446
            const dataTransfer = new DataTransfer();
            acceptedFiles.forEach((v) => {
                dataTransfer.items.add(v);
            });
            hiddenInputRef.current.files = dataTransfer.files;
        }
    }, [onChange])

    const dropAccepted = useCallback((event) => {
        onChange?.(event as any);
    }, [onChange])

    const {acceptedFiles, fileRejections, getRootProps, getInputProps, open, isDragActive} = useDropzone({
        // noClick: true,
        noKeyboard: true,
        accept: ACCEPTED_FILES,
        maxFiles: 1,
        onDrop: dropHandler,
        onDropAccepted: dropAccepted
    });

    const disableHiddenFieldEvents = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
    }, [])

    return (
        <div className="file-field-md">
            <input
                type="file"
                name={name}
                required={required}
                ref={hiddenInputRef}
                onClick={disableHiddenFieldEvents}
                hidden={props.hidden}
                disabled={props.disabled}
            />
            <div className="dnd-container" {...getRootProps()}>
                <input {...getInputProps()}/>
                <div className="dnd-message">
                    <UploadIcon/>
                    {`${t("forms.file-field-drag-and-drop-msg")} ${Object.values(ACCEPTED_FILES).flatMap(types => types).join(", ")}`}
                </div>
            </div>
            <div className="files-list">
                {
                    acceptedFiles.map(file => {
                        return (
                            <div className="uploaded-file">
                                <FileIcon/>
                                <span>{file.name} - {bytesToMegabytes(file.size)} Mb</span>
                            </div>
                        )
                    })
                }
            </div>
        </div>
    )
};

const FileFieldLabeled = FieldWithLabel(FileField);

export {FileFieldLabeled as Labeled, FileField as Field};

export default FileField;
